import { invalidate, ThreeEvent, useThree } from "@react-three/fiber";
import { editorStore } from "../../store/editorStore";
import { floorplannerStore } from "../../store/floorplannerStore";
import { userStore } from "../../store/userStore";
import { WallConnectionEnd, WallConnectionStart, WallType } from "../../types/floorplannerTypes";
import { MeasurementLine } from "./MeasurementLine";
import { MiddleHHandle } from "./MiddleHHandle";
import { MiddleVHandle } from "./MiddleVHandle";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, MutableRefObject } from "react";
import * as THREE from "three";
import { updateDragHandles } from "./updateDragHandles";
import { projectToWorld } from "./projectToWorld";
import { action } from "mobx";
import { dragStartHandle } from "./dragStartHandle";
import { dragEndHandle } from "./dragEndHandle";
import { dragMiddleVHandle } from "./dragMiddleVHandle";
import { dragMiddleHHandle } from "./dragMiddleHHandle";
import { dragWallHandle } from "./dragWallHandle";
import { dragControlHandle } from "./dragControlHandle";
import { updateAlignmentLines } from "./updateAligmentLines";
import { updateWallConnections, updateSymbolAttachmentsDuringDrag } from "./updateWallConnections";
import { AttachmentIndicator, useAttachmentIndicators } from "./AttachmentIndicator";
import {
    endpointHandleHitSize,
    getMiddleHandleSizeForLength,
    getPreferredWallMiddleHandleSize,
    handleSize,
} from "./createMiddleDragHandle";
import { useDragSelectedObjects } from "../../hooks/useDragSelectedObjects";
import { disposeMeshes } from "./disposeMeshes";
import { fillMeshWall } from "./fillMeshWalls";
import { observer } from "mobx-react-lite";
import { renderStore } from "../../store/renderStore";
import { wallUpdateManager } from "../../utils/wallUpdateManager";
import performanceMonitor from "../../utils/PerformanceMonitor";
import Vector2Pool from "../../utils/Vector2Pool";
import DragStateManager from "../../utils/DragStateManager";
import { registerWallInteractionHandlers } from "../../utils/wallInteractionRegistry";
import { getQuadraticBezierPoint } from "../../utils/arcUtils";
import { CurveDragHandle } from "./CurveDragHandle";
import { WallAddHandle } from "./WallAddHandle";
import { SELECTED_HANDLE_INTERACTION_Z, SELECTED_HANDLE_RENDER_ORDER } from "../../constants/canvas";
import { CANVAS_ACCENT_COLOR, CANVAS_HOVER_COLOR, canvasHandleFillColor } from "../../constants/brandColors";
import {
    getWallHoverAddButtonPose,
    WallHoverAddButtonPose,
} from "../../utils/wallHoverAddButton";

/** Keep endpoint free-move rings above mid-handles for hit-testing on short walls. */
const ENDPOINT_HANDLE_INTERACTION_Z = SELECTED_HANDLE_INTERACTION_Z + 0.01;

interface WallShapeProps {
    wall: WallType;
}

/**
 * WallShape component that renders a wall's fill mesh and drag handles.
 *
 * This component manages the wall mesh creation and drag interaction. It renders:
 * - The filled wall geometry (inner shape)
 * - Endcap outlines (always rendered)
 * - Drag handles for manipulation
 *
 * The wall mesh only includes fill + endcaps via fillMeshWalls.
 * WallOutlineStructure renders left/right outlines as segmented lines
 * with gaps at connection points.
 *
 * The component does not need to know about outline segmentation details - those are
 * handled by WallOutlineStructure.
 */
export const WallShape = observer((props: WallShapeProps) => {
    const {
        wall,
    } = props;
    const {
        wallsMap,
        wallWidth,
        hoverColor,
        lineColor,
        fillColor,
    } = floorplannerStore;
    const { scene, camera, gl } = useThree();
    const dragHandle = useRef<{
        handle: string;
        startPosition: THREE.Vector3;
        endPosition: THREE.Vector3;
    }>({
        handle: "",
        startPosition: new THREE.Vector3(),
        endPosition: new THREE.Vector3(),
    });
    const dragWall = useRef<WallType | null>(null);
    const dragOffset = useRef<[number, number, number, number] | null>(null);
    const [dragEndFreeToConnect, setDragEndFreeToConnect] = useState(false);
    const [hoveredHandle, setHoveredHandle] = useState<string | null>(null);
    const [dragPosition, setDragPosition] = useState<THREE.Vector2 | null>(null);
    const [addButtonPose, setAddButtonPose] = useState<WallHoverAddButtonPose | null>(null);
    const hoveringAddButton = useRef(false);
    const pendingAddButtonPose = useRef<WallHoverAddButtonPose | null>(null);
    const clearAddButtonTimeout = useRef<number | null>(null);
    const showAddButtonTimeout = useRef<number | null>(null);
    const attachmentPoints = useAttachmentIndicators(wall.id, dragPosition);
    const isHovered = (handle: string) => hoveredHandle === handle;
    // Admin-only while the midpoint "+" flow is tested in production.
    const canShowWallAddButton =
        userStore.isAdmin &&
        !editorStore.wallConstructionMode &&
        !editorStore.lineConstructionMode &&
        !editorStore.rulerConstructionMode &&
        !editorStore.areaConstructionMode &&
        !editorStore.areaFillConstructionMode &&
        !editorStore.areaBleedFillMode &&
        !editorStore.view3D &&
        !renderStore.isDragging &&
        !editorStore.wallDragging &&
        !floorplannerStore.lockedByOther &&
        !hoveredHandle &&
        !wall.selected;

    const cancelClearWallAddButton = useCallback(() => {
        if (clearAddButtonTimeout.current !== null) {
            window.clearTimeout(clearAddButtonTimeout.current);
            clearAddButtonTimeout.current = null;
        }
    }, []);

    const cancelShowWallAddButton = useCallback(() => {
        if (showAddButtonTimeout.current !== null) {
            window.clearTimeout(showAddButtonTimeout.current);
            showAddButtonTimeout.current = null;
        }
    }, []);

    const scheduleClearWallAddButton = useCallback(() => {
        cancelClearWallAddButton();
        cancelShowWallAddButton();
        // Brief grace so the pointer can move from the wall onto the offset "+" chip.
        clearAddButtonTimeout.current = window.setTimeout(() => {
            if (!hoveringAddButton.current) {
                pendingAddButtonPose.current = null;
                setAddButtonPose(null);
            }
            clearAddButtonTimeout.current = null;
        }, 200);
    }, [cancelClearWallAddButton, cancelShowWallAddButton]);

    const scheduleShowWallAddButton = useCallback(() => {
        if (showAddButtonTimeout.current !== null || addButtonPose) {
            return;
        }
        // Delay reveal so brief hovers for select/drag are not interrupted by the "+" chip.
        showAddButtonTimeout.current = window.setTimeout(() => {
            showAddButtonTimeout.current = null;
            if (pendingAddButtonPose.current) {
                setAddButtonPose(pendingAddButtonPose.current);
            }
        }, 650);
    }, [addButtonPose]);
    const noopRaycast = () => null;
    const groupRef = useRef<THREE.Group>(null);
    const groupRaycastTarget = useRef<THREE.Group | null>(null);
    const meshRaycastTarget = useRef<THREE.Object3D | null>(null);
    const originalGroupRaycast = useRef<any>(undefined);
    const originalMeshRaycast = useRef<any>(undefined);
    const { startDraggingGroupSelection } = useDragSelectedObjects(gl, camera);
    const [meshWall, setMeshWall] = useState<THREE.Group>(new THREE.Group());
    const dragOrigin = useRef<{ start: THREE.Vector2; end: THREE.Vector2 } | null>(null);
    const dragPreviewRef = useRef<THREE.Group | null>(null);
    const dragPointerOrigin = useRef<THREE.Vector2 | null>(null);
    const dragPointerOriginScreen = useRef<{ x: number; y: number } | null>(null);
    const aboutToDrag = useRef(false);
    const rafId = useRef<number | null>(null);
    const beginWallDragCalled = useRef(false);
    const hasDragged = useRef(false); // Track if actual drag movement occurred (threshold exceeded)
    const disablePointerEvents = editorStore.wallConstructionMode;
    const progressiveRenderTargets = useRef<Set<string>>(new Set());
    const lastAlignmentUpdateTime = useRef(0);
    const canApplyHandleHover = useCallback(() => {
        return (
            !aboutToDrag.current &&
            !renderStore.isDragging &&
            !editorStore.wallDragging &&
            dragHandle.current.handle === ""
        );
    }, []);

    const allowLocalWallVisualUpdates = (targetWall: WallType) => {
        const nextIds = new Set<string>([targetWall.id]);
        targetWall.centerConnections?.forEach(connection => {
            if (floorplannerStore.wallsMap.has(connection.id)) {
                nextIds.add(connection.id);
            }
        });
        targetWall.edgeConnections?.forEach(connection => {
            if (floorplannerStore.wallsMap.has(connection.targetWallId)) {
                nextIds.add(connection.targetWallId);
            }
        });

        // Remove any stale allowances from previous drags
        progressiveRenderTargets.current.forEach(id => {
            if (!nextIds.has(id)) {
                renderStore.disallowVisualUpdatesForWall(id);
            }
        });

        progressiveRenderTargets.current = nextIds;
        nextIds.forEach(id => renderStore.allowVisualUpdatesForWall(id));
    };

    const onWallSelectionPointerMove = useCallback(
        action((e: PointerEvent) => {
            if (dragWall.current !== null && dragOffset.current && editorStore.selections.length === 1) {
                // Cancel previous RAF request if pending
                if (rafId.current !== null) {
                    cancelAnimationFrame(rafId.current);
                }

                // Throttle position updates using RAF
                rafId.current = requestAnimationFrame(() => {
                    if (aboutToDrag.current) {
                        // Delta-based undo tracking is now handled in pointer down/up
                        aboutToDrag.current = false;
                    }
                    if (!editorStore.wallDragging) {
                        editorStore.setWallDragging(dragWall.current!.id);
                    }
                    let [newX, newY] = projectToWorld(e.clientX, e.clientY, gl, camera);
                    const wall = dragWall.current!;
                    const origin = dragPointerOrigin.current;
                    const originScreen = dragPointerOriginScreen.current;
                    const requiresThreshold = ["start", "end", "middleV", "middleH", "wall"].includes(dragHandle.current.handle);
                    if (origin && requiresThreshold && originScreen) {
                        const pointerDeltaWorld = Math.hypot(newX - origin.x, newY - origin.y);
                        const pointerDeltaPixels = Math.hypot(e.clientX - originScreen.x, e.clientY - originScreen.y);
                        const worldThreshold = dragHandle.current.handle === "wall" ? 0.05 : 0.02;
                        const pixelThreshold = dragHandle.current.handle === "wall" ? 6 : 3;
                        if (pointerDeltaWorld < worldThreshold && pointerDeltaPixels < pixelThreshold) {
                            rafId.current = null;
                            return;
                        }
                        // Threshold exceeded - mark as actually dragging
                        hasDragged.current = true;
                    }
                    const [startOffsetX, startOffsetY, endOffsetX, endOffsetY] =
                        dragOffset.current!;

                    // Middle handles should NOT call beginWallDrag as it clears connections
                    // They should only update positions without affecting connections
                    const isMiddleHandle = dragHandle.current.handle === "middleV" || dragHandle.current.handle === "middleH";

                    if (!beginWallDragCalled.current && !isMiddleHandle) {
                        renderStore.allowVisualUpdatesForWall(wall.id);
                        // Disconnect connections for wall, start, and end handle drags
                        // Start/end handles detach connections at the dragged endpoint during drag
                        const shouldDisconnect = dragHandle.current.handle === 'wall' ||
                            dragHandle.current.handle === 'start' ||
                            dragHandle.current.handle === 'end';
                        floorplannerStore.beginWallDrag(wall.id, {
                            disconnectConnections: shouldDisconnect,
                            deferDetachedWallRebuild: shouldDisconnect,
                        });
                        if (dragHandle.current.handle === "start" || dragHandle.current.handle === "end" || dragHandle.current.handle === "control") {
                            renderStore.clearSymbolClippings(wall.id);
                        }
                        renderStore.requestInvalidation(wall.id);
                        beginWallDragCalled.current = true;
                    }

                    let delta = new THREE.Vector2(0, 0);
                    if (dragHandle.current.handle === "start") {
                        dragStartHandle(wall, startOffsetX, startOffsetY, newX, newY, dragEndFreeToConnect, delta, true);
                    } else if (dragHandle.current.handle === "end") {
                        dragEndHandle(wall, endOffsetX, endOffsetY, newX, newY, dragEndFreeToConnect, delta, true);
                    } else if (dragHandle.current.handle === "middleV") {
                        dragMiddleVHandle(wall, startOffsetX, startOffsetY, newX, newY, delta, false);
                    } else if (dragHandle.current.handle === "middleH") {
                        dragMiddleHHandle(wall, startOffsetX, startOffsetY, newX, newY, delta, false);
                    } else if (dragHandle.current.handle === "wall") {
                        dragWallHandle(wall, startOffsetX, startOffsetY, endOffsetX, endOffsetY, newX, newY, delta, true);
                    } else if (dragHandle.current.handle === "control") {
                        dragControlHandle(wall, newX, newY);
                    }

                    // Refresh local wall reference with latest store data
                    const latestWall = floorplannerStore.findObjectId(wall.id) as WallType | undefined;
                    if (latestWall) {
                        dragWall.current = latestWall;
                    }

                    // For middle handle drags, skip symbol and outline updates for performance
                    // The visual feedback is handled by the wall mesh translation
                    // Full updates happen when drag ends
                    const handleType = dragHandle.current.handle;
                    if (handleType !== "middleV" && handleType !== "middleH") {
                        // Update symbol attachments during drag for real-time feedback (non-middle handles)
                        updateSymbolAttachmentsDuringDrag(wall.id, floorplannerStore);
                    }

                    const handleWall = latestWall ?? wall;
                    updateDragHandles(dragHandle.current?.handle || "", handleWall, dragHandle);

                    const alignmentPosition = (() => {
                        if (!latestWall) {
                            return { x: newX, y: newY };
                        }
                        if (dragHandle.current.handle === "start") {
                            return { x: latestWall.start.x, y: latestWall.start.y };
                        }
                        if (dragHandle.current.handle === "end") {
                            return { x: latestWall.end.x, y: latestWall.end.y };
                        }
                        return { x: newX, y: newY };
                    })();

                    // Skip alignment lines for middle handle drags - they iterate through ALL walls
                    // causing O(n²) performance issues in large floorplans
                    if (handleType !== "middleV" && handleType !== "middleH") {
                        const now = performance.now();
                        if (now - lastAlignmentUpdateTime.current > 50) {
                            lastAlignmentUpdateTime.current = now;
                            updateAlignmentLines(
                                wall.id,
                                alignmentPosition.x,
                                alignmentPosition.y,
                                dragHandle.current.handle,
                            );
                        }
                    }

                    // Update drag position for attachment indicators using object pool
                    const { vector: dragPos, release } = Vector2Pool.getTemporary(newX, newY);
                    setDragPosition(dragPos);
                    release(); // Release immediately since setDragPosition creates a copy

                    // For middle handles, ensure we trigger an update after the wall position changes
                    if (handleType === "middleV" || handleType === "middleH") {
                        // The wall position has been updated by the drag function
                        // Now we need to trigger a render update
                        const updatedWall = floorplannerStore.findObjectId(wall.id) as WallType | undefined;
                        if (updatedWall) {
                            dragWall.current = updatedWall;
                            floorplannerStore.syncAreaFillsForObjects([updatedWall.id]);

                            // Geometry is recomputed during middle-handle drags, so avoid double-applying
                            // the movement via preview translation. Just request an invalidation.
                            renderStore.requestInvalidation(wall.id);
                        }
                    } else if (handleType === "control" && latestWall) {
                        floorplannerStore.syncAreaFillsForObjects([latestWall.id]);
                        renderStore.allowVisualUpdatesForWall(wall.id);
                        renderStore.composeWallShape(wall.id, scene);
                        renderStore.requestInvalidation(wall.id);
                    }

                    rafId.current = null;
                });
            }
        }),
        [editorStore.wallDragging, projectToWorld, updateDragHandles, wallsMap, camera.zoom, dragEndFreeToConnect, scene],
    );

    const onWallSelectionPointerUp = useCallback(() => {
        aboutToDrag.current = false;

        // Clear drag position to hide attachment indicators
        setDragPosition(null);

        // Cancel any pending RAF request
        if (rafId.current !== null) {
            cancelAnimationFrame(rafId.current);
            rafId.current = null;
        }

        // Disable performance monitoring when drag movement ends (before cleanup operations)
        performanceMonitor.disableMonitoring();

        // End drag state management and execute deferred operations
        DragStateManager.endDrag();

        // Mark as no longer dragging EARLY so snapping logic works correctly
        // (snapToOtherObjects checks isDragging and skips if true)
        renderStore.setIsDragging(false);

        // Clear object pool to free memory after drag operations
        Vector2Pool.clear();

        // Update wall connections using deferred connection management
        const wallToUpdate = dragWall.current;
        if (wallToUpdate) {
            // Call endWallDrag to restore connections and perform final snapping (only if beginWallDrag was called)
            let affectedWallIds: string[] = [];
            if (beginWallDragCalled.current) {
                affectedWallIds = floorplannerStore.endWallDrag(wallToUpdate.id);
            }

            // Disable visual updates and progressive mode before performing connection updates
            renderStore.disallowVisualUpdatesForWall(wallToUpdate.id);

            // Get handle type and check if this was a middle handle drag
            const handle = dragHandle.current?.handle;
            const isMiddleHandle = handle === "middleV" || handle === "middleH";

            wallUpdateManager.enableProgressiveModeForWall(wallToUpdate.id, false);

            // For middle handle drags, force immediate geometry recomposition
            // This prevents the fill from disappearing after drop
            if (isMiddleHandle) {
                // Re-enable visual updates for the wall
                renderStore.allowVisualUpdatesForWall(wallToUpdate.id);
                affectedWallIds.forEach(wallId => {
                    renderStore.allowVisualUpdatesForWall(wallId);
                });

                // Force immediate recomposition of wall geometry
                renderStore.composeWallShape(wallToUpdate.id, scene);
                // Regenerate joint polygons for proper corner fills
                renderStore.generateJointPolygons(wallToUpdate.id);

                // Also force recomposition for all affected (connected) walls
                affectedWallIds.forEach(wallId => {
                    renderStore.composeWallShape(wallId, scene);
                    renderStore.generateJointPolygons(wallId);
                });

                // Trigger invalidation to ensure rendering updates
                renderStore.requestInvalidation(wallToUpdate.id);
                affectedWallIds.forEach(wallId => {
                    renderStore.requestInvalidation(wallId);
                });

                // Clean up the drag state that was created for middle handle drags
                floorplannerStore.wallDragState.delete(wallToUpdate.id);
            }

            // Perform final snapping to create connections now that drag is complete
            // Only snap if actual drag movement occurred (prevents jumping on click)
            if ((handle === "start" || handle === "end") && hasDragged.current) {
                const latestWall = floorplannerStore.findObjectId(wallToUpdate.id);
                if (latestWall && !editorStore.isShiftPressed) {
                    const connectionEnd = handle === "start" ? WallConnectionStart : WallConnectionEnd;
                    const position = handle === "start" ? latestWall.start : latestWall.end;

                    // Use edge snapping unless ALT is pressed
                    if (!editorStore.isAltPressed) {
                        const snapResult = floorplannerStore.snapToOtherObjectsEdgeBased(
                            latestWall.id,
                            connectionEnd,
                            position,
                            true,
                            { allowDuringDeferred: false }
                        );
                        if (typeof snapResult === "object" && snapResult.targetId && !affectedWallIds.includes(snapResult.targetId)) {
                            affectedWallIds.push(snapResult.targetId);
                        }
                    } else {
                        // Center-based snapping when ALT is pressed
                        const snapResult = floorplannerStore.snapToOtherObjects(
                            latestWall.id,
                            connectionEnd,
                            position,
                            true,
                            { allowDuringDeferred: false }
                        );
                        if (typeof snapResult === "object" && snapResult.targetId && !affectedWallIds.includes(snapResult.targetId)) {
                            affectedWallIds.push(snapResult.targetId);
                        }
                    }

                    const snappedWall = floorplannerStore.findObjectId(latestWall.id);
                    if (snappedWall && "edgeConnections" in snappedWall) {
                        snappedWall.edgeConnections?.forEach(connection => {
                            if (connection.targetWallId && !affectedWallIds.includes(connection.targetWallId)) {
                                affectedWallIds.push(connection.targetWallId);
                            }
                        });
                    }
                }
            }

            // Perform single comprehensive connection update
            updateWallConnections(
                wallToUpdate.id,
                dragHandle.current?.handle || null,
                [wallToUpdate.id, ...affectedWallIds],
                floorplannerStore
            );

            const wallsToFinalize = [wallToUpdate.id, ...affectedWallIds];
            const finalizeDropGeometry = () => {
                renderStore.finalizeJointGeometryForWalls(
                    wallsToFinalize,
                    'wall-drop',
                    scene
                );
            };

            renderStore.beginMultiJointRendering();
            if (typeof wallUpdateManager.processPendingUpdatesNow === 'function') {
                wallUpdateManager.processPendingUpdatesNow().catch(error => {
                    console.error('WallShape: Failed to flush wall updates after drag', {
                        wallId: wallToUpdate.id,
                        error
                    });
                }).then(finalizeDropGeometry).finally(() => {
                    renderStore.endMultiJointRendering();
                });
            } else {
                finalizeDropGeometry();
                renderStore.endMultiJointRendering();
            }

            // END DELTA-BASED UNDO TRACKING
            // Record the final state of modified walls after drag completes
            if (hasDragged.current) {
                floorplannerStore.recordWallChange(wallToUpdate.id, 'modify');
                affectedWallIds.forEach(id => {
                    floorplannerStore.recordWallChange(id, 'modify');
                });
                floorplannerStore.pushToUndoStack();
            } else {
                floorplannerStore.cancelUndoTransaction();
            }
        }

        if (progressiveRenderTargets.current.size > 0) {
            progressiveRenderTargets.current.forEach(id => {
                renderStore.disallowVisualUpdatesForWall(id);
            });
            progressiveRenderTargets.current.clear();
        }

        dragWall.current = null;

        // Re-enable main mesh visibility BEFORE removing preview to prevent visual gap
        meshWall.visible = true;

        const preview = dragPreviewRef.current;
        if (preview && scene) {
            scene.remove(preview);
            disposeMeshes(preview);
            preview.clear();
            dragPreviewRef.current = null;
        }
        dragOrigin.current = null;
        meshWall.position.set(0, 0, meshWall.position.z);
        invalidate();
        dragOffset.current = null;
        dragHandle.current.handle = "";
        dragPointerOrigin.current = null;
        dragPointerOriginScreen.current = null;
        beginWallDragCalled.current = false;
        hasDragged.current = false;
        lastAlignmentUpdateTime.current = 0;
        rafId.current = null;
        editorStore.setWallDragging(null);

        // No need to clear flags here as they're managed by the shape reconstruction process
        // Change cursor back to auto when dragging is done
        document.body.style.cursor = "auto";
        gl.domElement.removeEventListener("pointermove", onWallSelectionPointerMove);
        gl.domElement.removeEventListener("pointerup", onWallSelectionPointerUp);
        // Remove global fallback listeners
        window.removeEventListener("pointerup", onWallSelectionPointerUp);
        window.removeEventListener("pointercancel", onWallSelectionPointerUp);
        floorplannerStore.setBlockDirty(false);

        const processDeferredRoomUpdates = () => {
            floorplannerStore.setDeferRoomUpdates(false, { processPending: false });
            floorplannerStore.updateAffectedRooms();
            floorplannerStore.setDirty();
        };

        // Process deferred room updates after the wall connection/joint work has settled.
        // Running this inside the detach/reconnect event can rebuild the room index and
        // DFS room graph while React is also flushing the pointer interaction.
        if ('requestIdleCallback' in window) {
            requestIdleCallback(processDeferredRoomUpdates);
        } else {
            setTimeout(processDeferredRoomUpdates, 0);
        }

        // Defer this state update to avoid setState-in-render warnings
        setTimeout(() => {
            editorStore.setWhichEndToUpdate(undefined);
        }, 0);
    }, [gl.domElement, onWallSelectionPointerMove, scene]);

    const onWallSelectionPointerDown = useCallback(
        (
            event: ThreeEvent<PointerEvent>,
            handle: "start" | "end" | "middleV" | "middleH" | "control",
            id: string
        ) => {
            event.stopPropagation();
            setHoveredHandle(null);
            const wall = wallsMap.get(id);
            if (!wall) return;
            aboutToDrag.current = true;
            dragWall.current = wall;
            // Enable performance monitoring for any drag operation
            performanceMonitor.enableMonitoring();
            // Mark as dragging to defer room recalculations until drag ends
            renderStore.setIsDragging(true);
            floorplannerStore.setDeferRoomUpdates(true);

            // BEGIN DELTA-BASED UNDO TRACKING
            // Capture wall state BEFORE modification for efficient undo
            floorplannerStore.beginUndoTransaction(`drag wall ${handle}`);
            floorplannerStore.recordWallForUndo(id);
            // Also record connected walls that might be modified during drag
            wall.centerConnections?.forEach(conn => {
                floorplannerStore.recordWallForUndo(conn.id);
            });
            wall.edgeConnections?.forEach(conn => {
                floorplannerStore.recordWallForUndo(conn.targetWallId);
            });

            const suppressUpdates = handle === "middleV" || handle === "middleH";

            if (suppressUpdates) {
                // Build the set of allowed wall IDs: dragged wall + immediate connections
                const allowedWallIds = new Set<string>([wall.id]);

                // Add edge connections
                wall.edgeConnections?.forEach(conn => {
                    allowedWallIds.add(conn.targetWallId);
                });

                // Add center connections
                wall.centerConnections?.forEach(conn => {
                    allowedWallIds.add(conn.id);
                });

                // Start drag with suppressAllWallUpdates to prevent cascading updates
                DragStateManager.startDrag({
                    suppressWallUpdates: true,
                    suppressAllWallUpdates: true,
                    wallId: wall.id,
                    allowedWallIds,
                });
            } else {
                DragStateManager.startDrag({
                    suppressWallUpdates: false,
                    wallId: undefined
                });
            }

            dragOrigin.current = { start: wall.start.clone(), end: wall.end.clone() };
            if (suppressUpdates) {
                // Enable progressive mode for middle handle drags
                wallUpdateManager.enableProgressiveModeForWall(wall.id, true);
                allowLocalWallVisualUpdates(wall);

                // Set up minimal drag state with nearbyWallIds for snapping optimization
                // This avoids the O(n²) performance issue of scanning all walls during drag
                const margin = 2.0;
                const minX = Math.min(wall.start.x, wall.end.x) - margin;
                const maxX = Math.max(wall.start.x, wall.end.x) + margin;
                const minY = Math.min(wall.start.y, wall.end.y) - margin;
                const maxY = Math.max(wall.start.y, wall.end.y) + margin;

                const nearbyWallIds: string[] = [];
                for (const otherWall of floorplannerStore.wallsMap.values()) {
                    if (otherWall.id === wall.id) continue;
                    const wallMinX = Math.min(otherWall.start.x, otherWall.end.x);
                    const wallMaxX = Math.max(otherWall.start.x, otherWall.end.x);
                    const wallMinY = Math.min(otherWall.start.y, otherWall.end.y);
                    const wallMaxY = Math.max(otherWall.start.y, otherWall.end.y);
                    if (!(wallMaxX < minX || wallMinX > maxX || wallMaxY < minY || wallMinY > maxY)) {
                        nearbyWallIds.push(otherWall.id);
                    }
                }

                // Set minimal drag state for snapping optimization only
                floorplannerStore.wallDragState.set(wall.id, {
                    originalConnections: wall.centerConnections ? [...wall.centerConnections] : [],
                    originalEdgeConnections: wall.edgeConnections ? [...wall.edgeConnections] : [],
                    isDragging: true,
                    isCommitting: false,
                    visualSnapping: undefined,
                    nearbyWallIds,
                    alignmentSnap: {},
                    edgeSnapping: {},
                    alignmentLocks: {},
                    fixedEndpoints: {
                        start: wall.start.clone(),
                        end: wall.end.clone(),
                    },
                    pivotAnchors: {} as any,
                });

                // Generate alignment lines ONCE at drag start for snapping during drag
                // This avoids the O(n²) per-frame cost while still enabling snapping
                updateAlignmentLines(wall.id, wall.start.x, wall.start.y, "start");
                updateAlignmentLines(wall.id, wall.end.x, wall.end.y, "end");

                if (!dragPreviewRef.current && scene) {
                    const preview = meshWall.clone(true);
                    preview.name = `wall-drag-preview-${wall.id}`;
                    preview.position.copy(meshWall.position);
                    scene.add(preview);
                    dragPreviewRef.current = preview;
                    meshWall.visible = false;
                }
                meshWall.position.set(0, 0, meshWall.position.z);
                invalidate();
            }

            // Defer connection clearing until actual drag starts (only for start/end handles)
            if (handle === "start" || handle === "end") {
                renderStore.allowVisualUpdatesForWall(wall.id);
                renderStore.requestInvalidation(wall.id); // Trigger immediate visual update
                beginWallDragCalled.current = false;
            } else if (handle === "middleV" || handle === "middleH") {
                renderStore.allowVisualUpdatesForWall(wall.id);
                beginWallDragCalled.current = false; // Do NOT clear connections for middle handles
            } else if (handle === "control") {
                renderStore.allowVisualUpdatesForWall(wall.id);
                beginWallDragCalled.current = false;
            } else {
                beginWallDragCalled.current = false;
            }
            updateDragHandles(handle, wall, dragHandle);
            const [worldX, worldY] = projectToWorld(event.clientX, event.clientY, gl, camera);
            let offsetX, offsetY;

            if (handle === "middleV" || handle === "middleH" || handle === "control") {
                const midpointX = (wall.start.x + wall.end.x) / 2;
                const midpointY = (wall.start.y + wall.end.y) / 2;
                offsetX = worldX - midpointX;
                offsetY = worldY - midpointY;
            } else {
                offsetX = worldX - (handle === "start" ? wall.start.x : wall.end.x);
                offsetY = worldY - (handle === "start" ? wall.start.y : wall.end.y);
            }
            if (handle === "start" || handle === "end") {
                const isWallEdgeConnected = wall.centerConnections?.find(
                    (c) => (c.targetPosition === WallConnectionStart || c.targetPosition === WallConnectionEnd) &&
                        c.sourcePosition === (handle === 'start' ? WallConnectionStart : WallConnectionEnd),
                );
                setDragEndFreeToConnect(!isWallEdgeConnected);
            }

            dragOffset.current = [offsetX, offsetY, offsetX, offsetY];
            dragPointerOrigin.current = new THREE.Vector2(worldX, worldY);
            dragPointerOriginScreen.current = { x: event.clientX, y: event.clientY };
            if (!editorStore.isShiftPressed && editorStore.selections.find((s) => s.id === wall.id) === undefined) {
                editorStore.clearSelections();
            }
            editorStore.addSelection(wall);
            floorplannerStore.selectWall(wall.id);
            gl.domElement.addEventListener("pointermove", onWallSelectionPointerMove);
            gl.domElement.addEventListener("pointerup", onWallSelectionPointerUp);
            // Add global fallback listeners to prevent stuck deferred mode
            window.addEventListener("pointerup", onWallSelectionPointerUp);
            window.addEventListener("pointercancel", onWallSelectionPointerUp);
            // Set pointer capture for reliable event delivery
            if (event.currentTarget && 'setPointerCapture' in event.currentTarget) {
                try {
                    (event.currentTarget as any).setPointerCapture(event.pointerId);
                } catch {
                    // Ignore errors if pointer capture fails
                }
            }
            floorplannerStore.setBlockDirty(true);
        },
        [
            wallsMap,
            updateDragHandles,
            projectToWorld,
            gl.domElement,
            onWallSelectionPointerMove,
            onWallSelectionPointerUp,
            camera.zoom,
            renderStore.requestInvalidation
        ],
    );

    const updateWallAddButtonPose = useCallback(
        (event: ThreeEvent<PointerEvent>) => {
            if (
                !canShowWallAddButton ||
                editorStore.wallConstructionMode ||
                editorStore.lineConstructionMode ||
                editorStore.rulerConstructionMode ||
                editorStore.areaConstructionMode ||
                editorStore.areaFillConstructionMode ||
                editorStore.areaBleedFillMode ||
                editorStore.view3D ||
                renderStore.isDragging ||
                editorStore.wallDragging ||
                floorplannerStore.lockedByOther
            ) {
                cancelClearWallAddButton();
                cancelShowWallAddButton();
                pendingAddButtonPose.current = null;
                setAddButtonPose(null);
                return;
            }

            cancelClearWallAddButton();
            // Prefer screen→world projection: outline Line2 hits can report unstable event.point.
            const world = projectToWorld(event.clientX, event.clientY, gl, camera);
            const pointer = new THREE.Vector2(world[0], world[1]);
            const pose = getWallHoverAddButtonPose(
                wall,
                pointer,
                wallWidth,
                editorStore.zoomLevelDivisor(),
            );
            pendingAddButtonPose.current = pose;
            if (!pose) {
                cancelShowWallAddButton();
                setAddButtonPose(null);
                return;
            }
            // Follow hover along the wall centerline; delay only gates the first reveal.
            if (addButtonPose) {
                setAddButtonPose(pose);
            } else {
                scheduleShowWallAddButton();
            }
        },
        [
            wall,
            wallWidth,
            gl,
            camera,
            canShowWallAddButton,
            addButtonPose,
            cancelClearWallAddButton,
            cancelShowWallAddButton,
            scheduleShowWallAddButton,
        ],
    );

    const onWallPointerEnter = useCallback(
        (event: ThreeEvent<PointerEvent>) => {
            // Skip hover events during drag operations for better performance
            if (renderStore.isDragging || editorStore.wallDragging) {
                return;
            }

            event.stopPropagation();
            // Change cursor to grab when hovering over a wall
            if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") document.body.classList.add("cursor-vector");;
            let updated = false;

            // Get the wall data to check if it's selected
            //const wallData = walls[Object.keys(walls)[index]];

            // Iterate through the wall's children and apply hoverColor to lines
            meshWall.children.forEach((child) => {
                const mesh = child as THREE.Mesh;
                if (child.userData.type === "line") {
                    const currentColor = (mesh.material as THREE.MeshBasicMaterial).color.getHex();

                    // Always apply hoverColor when hovered, whether selected or not
                    if (currentColor !== hoverColor) {
                        (mesh.material as THREE.MeshBasicMaterial).color.set(hoverColor);
                        updated = true;
                    }
                }
            });
            if (updated && !renderStore.isReconstructingShapes) {
                renderStore.requestInvalidation(wall.id);
            }
            updateWallAddButtonPose(event);
        },
        [hoverColor, meshWall, wallsMap, renderStore.requestInvalidation, updateWallAddButtonPose],
    );

    const onWallPointerMove = useCallback(
        (event: ThreeEvent<PointerEvent>) => {
            if (renderStore.isDragging || editorStore.wallDragging) {
                return;
            }
            updateWallAddButtonPose(event);
        },
        [updateWallAddButtonPose],
    );

    const onWallPointerLeave = useCallback(
        (event: ThreeEvent<PointerEvent>, id: string) => {
            // Skip hover events during drag operations for better performance
            if (renderStore.isDragging || editorStore.wallDragging) {
                return;
            }

            event.stopPropagation();
            // Reset cursor when leaving a wall (unless over the "+" chip).
            if (
                !hoveringAddButton.current &&
                document.body.style.cursor !== "grabbing" &&
                document.body.style.cursor !== "move"
            ) {
                document.body.classList.remove("cursor-vector");
            }
            // Delay clear so the pointer can move onto the offset "+" button.
            if (!hoveringAddButton.current) {
                scheduleClearWallAddButton();
            }
            let updated = false;

            // Get the wall data to check if it's selected
            //const wallData = walls[Object.keys(walls)[index]];
            const wallData = wallsMap.get(id);

            meshWall.children.forEach((child) => {
                const mesh = child as THREE.Mesh;
                if (child.userData.type === "line") {
                    const currentColor = (mesh.material as THREE.MeshBasicMaterial).color.getHex();

                    // If the wall is selected, keep the hoverColor
                    // Otherwise, revert back to the default line color
                    //const newColor = wallData?.selected ? hoverColor : (walls[Object.keys(walls)[index]]?.lineColor || lineColor);
                    const newColor = wallData?.selected ? hoverColor : (wallData?.lineColor || lineColor);

                    if (currentColor !== newColor) {
                        (mesh.material as THREE.MeshBasicMaterial).color.set(newColor);
                        updated = true;
                    }
                }
            });
            if (updated && !renderStore.isReconstructingShapes) {
                renderStore.requestInvalidation(wall.id);
            }
        },
        [meshWall, wallsMap, lineColor, hoverColor, renderStore.requestInvalidation, scheduleClearWallAddButton],
    );

    const onWallAddButtonClick = useCallback(() => {
            if (!addButtonPose || floorplannerStore.lockedByOther) {
                return;
            }
            cancelClearWallAddButton();
            cancelShowWallAddButton();
            hoveringAddButton.current = false;
            pendingAddButtonPose.current = null;
            editorStore.startWallConstructionFromPoint(
                addButtonPose.startPoint,
                wall.id,
                addButtonPose.attachEndpoint,
                addButtonPose.projectedPoint,
                addButtonPose.outwardDirection,
            );
            setAddButtonPose(null);
        },
        [addButtonPose, wall.id, cancelClearWallAddButton, cancelShowWallAddButton],
    );

    const onWallPointerDown = useCallback(
        (event: ThreeEvent<PointerEvent>, id: string) => {
            if (floorplannerStore.lockedByOther) {
                event.stopPropagation();
                const wall = wallsMap.get(id);
                if (!wall) return;
                if (!editorStore.isShiftPressed) {
                    editorStore.clearSelections();
                }
                editorStore.addSelection(wall);
                floorplannerStore.selectWall(wall.id);
                return;
            }
            event.stopPropagation();
            aboutToDrag.current = true;

            const wall = wallsMap.get(id);
            if (!wall) return;

            // Enable performance monitoring for any drag operation
            performanceMonitor.enableMonitoring();

            // Start drag state management to defer heavy operations
            DragStateManager.startDrag({ suppressWallUpdates: true, wallId: wall.id });
            dragOrigin.current = { start: wall.start.clone(), end: wall.end.clone() };

            // Check if this wall is part of a group and if the group is not fully selected
            let shouldSelectGroup = false;
            if (!wall.selected) {
                const groupId = floorplannerStore.getObjectGroup(id);
                if (groupId) {
                    // Check if all group members are selected
                    const groupObjects = floorplannerStore.getGroupObjects(groupId);
                    const allGroupSelected = groupObjects.every(obj =>
                        editorStore.selections.some(sel => sel.id === obj.id)
                    );
                    shouldSelectGroup = !allGroupSelected;
                }
            }

            // Select the group if needed
            if (shouldSelectGroup && !wall.selected) {
                if (!editorStore.isShiftPressed) {
                    editorStore.clearSelections();
                }
                editorStore.addSelection(wall);
                floorplannerStore.selectWall(wall.id);
                // After selecting the group, immediately start group dragging
                DragStateManager.endDrag();
                performanceMonitor.disableMonitoring();
                dragOrigin.current = null;
                startDraggingGroupSelection(event);
                return;
            }

            // If we now have multiple selections, check if this wall is part of the selection
            if (editorStore.selections.length > 1) {
                // Only use group drag if this wall is part of the current selection
                if (wall.selected) {
                    DragStateManager.endDrag();
                    performanceMonitor.disableMonitoring();
                    dragOrigin.current = null;
                    startDraggingGroupSelection(event);
                    return;
                }
                // If this wall is not selected, continue with normal selection logic below
            }

            if (!editorStore.isShiftPressed && editorStore.selections.find((s) => s.id === wall.id) === undefined) {
                editorStore.clearSelections();
            }
            if (editorStore.selections.find((s) => s.id === wall.id) === undefined) {
                editorStore.addSelection(wall);
                floorplannerStore.selectWall(wall.id);
            }

            DragStateManager.endDrag();
            performanceMonitor.disableMonitoring();
            dragOrigin.current = null;
            startDraggingGroupSelection(event);
            return;
        },
        [
            wallsMap,
            updateDragHandles,
            projectToWorld,
            gl.domElement,
            onWallSelectionPointerDown,
            onWallSelectionPointerUp,
            meshWall,
            hoverColor,
            camera.zoom,
            renderStore.requestInvalidation,
            scene
        ],
    );

    useEffect(() => {
        const unregister = registerWallInteractionHandlers(wall.id, {
            onPointerDown: (event) => onWallPointerDown(event, wall.id),
            onPointerEnter: (event) => onWallPointerEnter(event),
            onPointerMove: (event) => onWallPointerMove(event),
            onPointerLeave: (event) => onWallPointerLeave(event, wall.id),
        });
        return unregister;
    }, [wall.id, onWallPointerDown, onWallPointerEnter, onWallPointerMove, onWallPointerLeave]);

    useEffect(() => {
        if (!canShowWallAddButton && addButtonPose) {
            cancelClearWallAddButton();
            cancelShowWallAddButton();
            pendingAddButtonPose.current = null;
            setAddButtonPose(null);
        }
    }, [canShowWallAddButton, addButtonPose, cancelClearWallAddButton, cancelShowWallAddButton]);

    useEffect(() => {
        return () => {
            cancelClearWallAddButton();
            cancelShowWallAddButton();
        };
    }, [cancelClearWallAddButton, cancelShowWallAddButton]);

    // Effect 1: Request shape updates when geometry/style inputs change (but NOT on lastUpdate)
    useEffect(() => {
        // Skip geometry updates during translate mode - we only apply visual offset
        if (renderStore.isObjectTranslating(wall.id)) {
            return;
        }
        const inner = renderStore.innerWallShapesMap.get(wall.id);
        const o1 = renderStore.outline1WallShapesMap.get(wall.id);
        const o2 = renderStore.outline2WallShapesMap.get(wall.id);
        const caps = renderStore.endCapsWallShapesMap.get(wall.id);
        if (!inner || !o1 || !o2 || !caps) {
            renderStore.updateShapes(wall.id, scene);
        }
        // Intentionally avoid depending on the entire `wall` object to prevent
        // cascading updates when only `lastUpdate` changes.
        // However, we need to include line properties (lineType, lineWeight, lineColor) so
        // outline segments are regenerated when these change.
    }, [wall.id, wall.start, wall.end, wallWidth, lineColor, fillColor, wall.fillColor, wall.fillOpacity, wall.fillPattern, wall.lineType, wall.lineWeight, wall.lineColor, scene, renderStore.isGroupTranslating]);

    // Effect 2: Rebuild mesh group when shapes have been updated (tracked via lastUpdate)
    // Use useLayoutEffect to run synchronously before browser paint, preventing flash
    // when group translate ends and offset is cleared but mesh hasn't updated yet
    useLayoutEffect(() => {
        // Skip mesh rebuilds during translate mode - we only apply visual offset
        if (renderStore.isObjectTranslating(wall.id)) {
            return;
        }
        // Only redraw when it's safe to do so (shapes are fully constructed)
        if (!renderStore.isVisualUpdateAllowed(wall.id)) {
            return;
        }

        const innerShapes = renderStore.innerWallShapesMap.get(wall.id);
        const outline1 = renderStore.outline1WallShapesMap.get(wall.id);
        const outline2 = renderStore.outline2WallShapesMap.get(wall.id);
        const endCaps = renderStore.endCapsWallShapesMap.get(wall.id);
        if (!innerShapes || !outline1 || !outline2 || !endCaps) return;

        // Create new mesh first
        const newMeshWall: THREE.Group = new THREE.Group();
        try {
            fillMeshWall(wall, innerShapes, outline1, outline2, endCaps, newMeshWall, floorplannerStore, scene);
        } catch (fillError) {
            console.error('WallShape: Failed to compose wall mesh', { wallId: wall.id, error: fillError });
        }
        if (dragPreviewRef.current) {
            newMeshWall.visible = false;
            const existingPreview = dragPreviewRef.current;
            if (existingPreview && scene) {
                const previewPosition = existingPreview.position.clone();
                const previewRotation = existingPreview.rotation.clone();
                const previewScale = existingPreview.scale.clone();
                const previewName = existingPreview.name || `wall-drag-preview-${wall.id}`;

                scene.remove(existingPreview);
                disposeMeshes(existingPreview);
                existingPreview.clear();

                const updatedPreview = newMeshWall.clone(true);
                updatedPreview.name = previewName;
                updatedPreview.position.copy(previewPosition);
                updatedPreview.rotation.copy(previewRotation);
                updatedPreview.scale.copy(previewScale);
                updatedPreview.visible = true;
                scene.add(updatedPreview);
                dragPreviewRef.current = updatedPreview;
            }
        }

        // Set the new mesh immediately to prevent flashing
        setMeshWall(newMeshWall);

        // Dispose the old mesh after the new one is set (deferred cleanup)
        // Use DragStateManager to defer disposal during drag operations
        const oldMeshWall = meshWall;
        DragStateManager.executeOrDefer(() => {
            if ('requestIdleCallback' in window) {
                requestIdleCallback(() => {
                    disposeMeshes(oldMeshWall);
                    oldMeshWall.clear();
                });
            } else {
                // Fallback to setTimeout for older browsers
                setTimeout(() => {
                    disposeMeshes(oldMeshWall);
                    oldMeshWall.clear();
                }, 0);
            }
        });

        return () => {
            if ('requestIdleCallback' in window) {
                requestIdleCallback(() => {
                    disposeMeshes(meshWall);
                    meshWall.clear();
                });
            } else {
                setTimeout(() => {
                    disposeMeshes(meshWall);
                    meshWall.clear();
                }, 0);
            }
        };
    }, [wall.id, wall.lastUpdate, wall.fillColor, wall.fillOpacity, wall.fillPattern, disposeMeshes, scene, renderStore.safeToRedraw, renderStore.requestInvalidation, renderStore.isGroupTranslating]);

    useEffect(() => {
        const updateRaycast = <T extends THREE.Object3D>(
            obj: T | null,
            cacheRef: MutableRefObject<any>,
            targetRef: MutableRefObject<T | null>,
        ) => {
            if (!obj) return;
            if (targetRef.current !== obj) {
                cacheRef.current = obj.raycast;
                targetRef.current = obj;
            }
            obj.raycast = disablePointerEvents ? noopRaycast : cacheRef.current;
        };

        updateRaycast(groupRef.current, originalGroupRaycast, groupRaycastTarget);
        updateRaycast(meshWall, originalMeshRaycast, meshRaycastTarget);
    }, [disablePointerEvents, meshWall]);

    // Calculate translate offset for optimized group drag
    const translateOffset = renderStore.isObjectTranslating(wall.id)
        ? renderStore.groupTranslateOffset
        : { x: 0, y: 0 };

    // Calculate rotation offset for optimized group rotation
    const isRotating = renderStore.isWallRotating(wall.id);
    const rotationData = isRotating
        ? { angle: renderStore.groupRotationAngle, center: renderStore.groupRotationCenter }
        : null;

    // Calculate final position with both translate and rotation offsets
    // For rotation around center C by angle θ, we need: position = C - R(θ)*C + translateOffset
    let finalPosition = [translateOffset.x, translateOffset.y, 0] as [number, number, number];
    let groupRotation = [0, 0, 0] as [number, number, number];
    if (rotationData) {
        const { angle, center } = rotationData;
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        // Compensate for rotation around arbitrary center point
        const rotationCompX = center.x * (1 - cos) + center.y * sin;
        const rotationCompY = center.y * (1 - cos) - center.x * sin;
        finalPosition = [translateOffset.x + rotationCompX, translateOffset.y + rotationCompY, 0];
        groupRotation = [0, 0, angle];
    }
    const wallLength = wall.start.distanceTo(wall.end);
    const middleHandleSize = getMiddleHandleSizeForLength(
        wallLength,
        getPreferredWallMiddleHandleSize(wallWidth),
    );
    const endpointHitSize = endpointHandleHitSize();
    const middleHandleCenter = wall.lineForm === "arc" && wall.controlPoint
        ? getQuadraticBezierPoint(0.5, wall.start, wall.controlPoint, wall.end)
        : wall.start.clone().add(wall.end).multiplyScalar(0.5);
    const middleHandleRotation = new THREE.Vector2().subVectors(wall.end, wall.start).angle();
    const selectionHandlesVisible = editorStore.shouldShowSelectionHandles();

    return (
        <group
            ref={groupRef}
            position={finalPosition}
            rotation={groupRotation}
            key={`group-wall-${meshWall.userData.id}`}
            onPointerEnter={!editorStore.wallConstructionMode && !editorStore.lineConstructionMode && !editorStore.rulerConstructionMode && !renderStore.isDragging && !editorStore.wallDragging ? (event) => onWallPointerEnter(event) : undefined}
            onPointerMove={!editorStore.wallConstructionMode && !editorStore.lineConstructionMode && !editorStore.rulerConstructionMode && !renderStore.isDragging && !editorStore.wallDragging ? (event) => onWallPointerMove(event) : undefined}
            onPointerLeave={!editorStore.wallConstructionMode && !editorStore.lineConstructionMode && !editorStore.rulerConstructionMode && !renderStore.isDragging && !editorStore.wallDragging ? (event) => onWallPointerLeave(event, wall.id) : undefined}
            onPointerDown={!editorStore.wallConstructionMode && !editorStore.lineConstructionMode && !editorStore.rulerConstructionMode ? (event) => onWallPointerDown(event, wall.id) : undefined}
        >
            {/* Wall mesh (fill + endcaps)
             *
             * meshWall includes fill + endcaps (left/right outlines are rendered
             * separately by WallOutlineStructure component as segmented lines)
             */}
            <primitive object={meshWall} />
            {/* Parked "+" after hover pause; wall body stays select/drag only. */}
            {canShowWallAddButton && addButtonPose && (
                <WallAddHandle
                    position={addButtonPose.buttonPoint}
                    wallWidth={wall.wallWidth ?? wallWidth}
                    onAdd={onWallAddButtonClick}
                    onHoverChange={(hovered) => {
                        hoveringAddButton.current = hovered;
                        if (hovered) {
                            cancelClearWallAddButton();
                            cancelShowWallAddButton();
                            document.body.classList.remove("cursor-vector");
                        } else {
                            scheduleClearWallAddButton();
                        }
                    }}
                />
            )}
            {!wall.hideMeasurement && 
                // Hide wall measurement when an attachment (door/window/void) is being dragged on this wall
                editorStore.attachmentDraggingOnWall !== wall.id &&
                (editorStore.showMeasures ||
                wall?.selected ||
                editorStore.wallEditingOuterLength === wall.id ||
                editorStore.wallEditingInnerLength === wall.id ||
                (editorStore.selections.length && editorStore.selections[0].id === wall.id)) && (
                    <MeasurementLine
                        object={wall}
                    />
                )}
            {(selectionHandlesVisible && wall.selected && !editorStore.wallConstructionMode && !editorStore.lineConstructionMode && !editorStore.rulerConstructionMode && !editorStore.areaConstructionMode && !editorStore.areaFillConstructionMode && !editorStore.areaBleedFillMode && editorStore.selections.length === 1) && (
                <group renderOrder={SELECTED_HANDLE_RENDER_ORDER}>
                    {/* Middle, start, and end handles */}
                    {(editorStore.wallDragging !== wall.id || dragHandle.current?.handle === "middleV") && (
                        <MiddleVHandle
                            wall={wall}
                            wallWidth={middleHandleSize}
                            centerPoint={middleHandleCenter}
                            onHandlePointerDown={(event: ThreeEvent<PointerEvent>) => onWallSelectionPointerDown(event, "middleV", wall.id)}
                            onHandlePointerEnter={(event: ThreeEvent<PointerEvent>) => {
                                event.stopPropagation();
                                if (!canApplyHandleHover()) return;
                                document.body.classList.add("cursor-vector")
                                setHoveredHandle("middleV")
                            }}
                            onHandlePointerLeave={(event: ThreeEvent<PointerEvent>) => {
                                event.stopPropagation();
                                if (editorStore.wallDragging !== wall.id) document.body.classList.remove("cursor-vector")
                                setHoveredHandle(null)
                            }}
                            fillColor={canvasHandleFillColor(dragHandle.current?.handle === "middleV", isHovered("middleV"))}
                            color={isHovered("middleV") || dragHandle.current?.handle === "middleV" ? "white" : "black"}
                        />
                    )}
                    {(editorStore.wallDragging !== wall.id || dragHandle.current?.handle === "middleH") && (
                        <MiddleHHandle
                            wall={wall}
                            wallWidth={middleHandleSize}
                            centerPoint={middleHandleCenter}
                            onHandlePointerDown={(event: ThreeEvent<PointerEvent>) => onWallSelectionPointerDown(event, "middleH", wall.id)}
                            onHandlePointerEnter={(event: ThreeEvent<PointerEvent>) => {
                                event.stopPropagation();
                                if (!canApplyHandleHover()) return;
                                document.body.classList.add("cursor-vector")
                                setHoveredHandle("middleH")
                            }}
                            onHandlePointerLeave={(event: ThreeEvent<PointerEvent>) => {
                                event.stopPropagation();
                                if (editorStore.wallDragging !== wall.id) document.body.classList.remove("cursor-vector")
                                setHoveredHandle(null)
                            }}
                            fillColor={canvasHandleFillColor(dragHandle.current?.handle === "middleH", isHovered("middleH"))}
                            color={isHovered("middleH") || dragHandle.current?.handle === "middleH" ? "white" : "black"}
                        />
                    )}
                    {(editorStore.wallDragging !== wall.id || dragHandle.current?.handle === "start") && (
                        <group
                            position={new THREE.Vector3(wall.start.x, wall.start.y, ENDPOINT_HANDLE_INTERACTION_Z)}
                            renderOrder={SELECTED_HANDLE_RENDER_ORDER + 3}
                            onPointerDown={(event) => onWallSelectionPointerDown(event, "start", wall.id)}
                            onPointerEnter={(event) => {
                                event.stopPropagation();
                                if (!canApplyHandleHover()) return;
                                document.body.style.cursor = "move";
                                setHoveredHandle("start");
                            }}
                            onPointerLeave={(event) => {
                                event.stopPropagation();
                                if (editorStore.wallDragging !== wall.id) document.body.style.cursor = "auto";
                                setHoveredHandle(null);
                            }}
                        >
                            <mesh renderOrder={SELECTED_HANDLE_RENDER_ORDER + 3}>
                                <planeGeometry args={[endpointHitSize, endpointHitSize]} />
                                <meshBasicMaterial
                                    // color="white"
                                    transparent
                                    opacity={0.0}
                                    depthTest={false}
                                    depthWrite={false}
                                />
                            </mesh>
                            <mesh raycast={noopRaycast} renderOrder={SELECTED_HANDLE_RENDER_ORDER + 4}>
                                <torusGeometry args={[handleSize / editorStore.zoomLevelDivisor() / 1.8, 0.003, 16, 100]} />
                                <meshBasicMaterial color={CANVAS_ACCENT_COLOR} depthTest={false} depthWrite={false} />
                            </mesh>
                            <mesh
                                position={[0, 0, 0]}
                                rotation={[Math.PI / 2, 0, 0]}
                                raycast={noopRaycast}
                                renderOrder={SELECTED_HANDLE_RENDER_ORDER + 5}
                            >
                                <cylinderGeometry args={[handleSize / editorStore.zoomLevelDivisor() / 1.8, 0, 0, 32]} />
                                <meshBasicMaterial color={isHovered("start") ? CANVAS_HOVER_COLOR : "white"} depthTest={false} depthWrite={false} />
                            </mesh>
                        </group>
                    )}

                    {(editorStore.wallDragging !== wall.id || dragHandle.current?.handle === "end") && (
                        <group
                            position={new THREE.Vector3(0, 0, ENDPOINT_HANDLE_INTERACTION_Z)}
                            renderOrder={SELECTED_HANDLE_RENDER_ORDER + 3}
                            onPointerDown={(event) => onWallSelectionPointerDown(event, "end", wall.id)}
                            onPointerEnter={(event) => {
                                event.stopPropagation();
                                if (!canApplyHandleHover()) return;
                                document.body.style.cursor = "move";
                                setHoveredHandle("end")
                            }}
                            onPointerLeave={(event) => {
                                event.stopPropagation();
                                if (editorStore.wallDragging !== wall.id) document.body.style.cursor = "auto";
                                setHoveredHandle(null)
                            }}
                        >
                            {/* Draw a circle grab handle for the end */}
                            <group position={new THREE.Vector3(wall.end.x, wall.end.y, 0)} renderOrder={SELECTED_HANDLE_RENDER_ORDER + 3}>
                                <mesh renderOrder={SELECTED_HANDLE_RENDER_ORDER + 3}>
                                    <planeGeometry args={[endpointHitSize, endpointHitSize]} />
                                    <meshBasicMaterial
                                        //color="white"
                                        transparent
                                        opacity={0.0}
                                        depthTest={false}
                                        depthWrite={false}
                                    />
                                </mesh>
                                <mesh raycast={noopRaycast} renderOrder={SELECTED_HANDLE_RENDER_ORDER + 4}>
                                    <torusGeometry args={[handleSize / editorStore.zoomLevelDivisor() / 1.8, 0.003, 16, 100]} />
                                    <meshBasicMaterial color={CANVAS_ACCENT_COLOR} depthTest={false} depthWrite={false} />
                                </mesh>
                                <mesh
                                    position={[0, 0, 0]}
                                    rotation={[Math.PI / 2, 0, 0]}
                                    raycast={noopRaycast}
                                    renderOrder={SELECTED_HANDLE_RENDER_ORDER + 5}
                                >
                                    <cylinderGeometry args={[handleSize / editorStore.zoomLevelDivisor() / 1.8, 0, 0, 32]} />
                                    <meshBasicMaterial color={isHovered("end") ? CANVAS_HOVER_COLOR : "white"} depthTest={false} depthWrite={false} />
                                </mesh>
                            </group>
                        </group>
                    )}

                    {(editorStore.wallDragging !== wall.id || dragHandle.current?.handle === "control") && (
                        <group
                            position={new THREE.Vector3(0, 0, SELECTED_HANDLE_INTERACTION_Z)}
                            renderOrder={SELECTED_HANDLE_RENDER_ORDER}
                            onPointerDown={(event) => onWallSelectionPointerDown(event, "control", wall.id)}
                            onPointerEnter={(event) => {
                                event.stopPropagation();
                                if (!canApplyHandleHover()) return;
                                document.body.style.cursor = "move";
                                setHoveredHandle("control")
                            }}
                            onPointerLeave={(event) => {
                                event.stopPropagation();
                                if (editorStore.wallDragging !== wall.id) document.body.style.cursor = "auto";
                                setHoveredHandle(null)
                            }}
                        >
                            <group
                                position={new THREE.Vector3(middleHandleCenter.x, middleHandleCenter.y, 0)}
                                rotation={[0, 0, middleHandleRotation]}
                                renderOrder={SELECTED_HANDLE_RENDER_ORDER}
                            >
                                <mesh raycast={noopRaycast} renderOrder={SELECTED_HANDLE_RENDER_ORDER}>
                                    <planeGeometry args={[middleHandleSize * 1.4, middleHandleSize * 1.4]} />
                                    <meshBasicMaterial transparent opacity={0.0} depthTest={false} depthWrite={false} />
                                </mesh>
                                <CurveDragHandle
                                    object={wall}
                                    size={middleHandleSize}
                                    fillColor={canvasHandleFillColor(dragHandle.current?.handle === "control", isHovered("control"))}
                                    color={isHovered("control") || dragHandle.current?.handle === "control" ? "white" : "black"}
                                />
                            </group>
                        </group>
                    )}

                    {/* Render attachment indicators during drag operations */}
                    {dragPosition && attachmentPoints.map(point => (
                        <AttachmentIndicator
                            key={point.id}
                            position={point.position}
                            visible={point.visible}
                            type={point.type}
                        />
                    ))}
                </group>
            )}
        </group>
    );
});
